import { FileText, MessageSquare, UserPlus, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { useUiStore, type WallpaperId } from "@/store";

const wallpaperClass: Record<WallpaperId, string> = {
  default: "chat-wallpaper",
  papaya: "chat-wallpaper-papaya",
  leaf: "chat-wallpaper-leaf",
  dusk: "chat-wallpaper-dusk",
};

export function ChatWelcomePanel() {
  const wallpaper = useUiStore((s) => s.wallpaper);

  return (
    <section
      className={cn(
        "relative flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center overflow-hidden px-4 py-8",
        wallpaperClass[wallpaper],
      )}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(251,170,51,0.12),transparent_42%),radial-gradient(circle_at_bottom,rgba(114,176,85,0.1),transparent_40%)]" />

      <div className="relative w-full max-w-[420px] rounded-[6px] border border-border bg-surface px-6 py-8 text-center shadow-[0_24px_60px_rgba(65,33,10,0.12)] dark:shadow-[0_24px_60px_rgba(0,0,0,0.45)] sm:px-8">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-papaya/15 text-papaya-deep">
          <MessageSquare className="h-7 w-7" strokeWidth={2.1} />
        </div>

        <h2 className="mt-6 text-xl font-semibold tracking-tight text-fg sm:text-2xl">
          Papaya Messages
        </h2>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-fg-muted">
          Select a conversation or tap + to start a direct message or group chat with your team.
        </p>
      </div>

      <div className="relative mt-8 flex flex-wrap items-start justify-center gap-8">
        <QuickAction icon={FileText} label="Send document" />
        <QuickAction icon={UserPlus} label="Add contact" />
        <QuickAction iconNode={<PapayaSparkIcon />} label="Ask Papaya AI" />
      </div>
    </section>
  );
}

function QuickAction({
  icon: Icon,
  iconNode,
  label,
}: {
  icon?: LucideIcon;
  iconNode?: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      className="group flex w-[88px] flex-col items-center gap-2 text-fg-muted transition-colors hover:text-fg"
    >
      <span className="inline-flex h-14 w-14 items-center justify-center rounded-[6px] border border-border bg-surface shadow-sm transition-colors group-hover:border-papaya/40 group-hover:bg-surface-muted">
        {iconNode ?? (Icon ? <Icon className="h-5 w-5" /> : null)}
      </span>
      <span className="text-center text-xs leading-tight">{label}</span>
    </button>
  );
}

function PapayaSparkIcon() {
  return (
    <svg viewBox="0 0 32 32" className="h-7 w-7" aria-hidden>
      <defs>
        <linearGradient id="papaya-ai" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbaa33" />
          <stop offset="55%" stopColor="#eb5e28" />
          <stop offset="100%" stopColor="#72b055" />
        </linearGradient>
      </defs>
      <circle cx="16" cy="16" r="15" fill="url(#papaya-ai)" />
      <path
        d="M16 7.5l1.4 4.2h4.4l-3.5 2.6 1.3 4.2L16 15.9l-3.6 2.6 1.3-4.2-3.5-2.6h4.4L16 7.5z"
        fill="#fff"
      />
    </svg>
  );
}
